import React, { useState, useEffect, useMemo } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import RoleGuard, { usePermission } from '../roles/RoleGuard';
import Modal from '../../components/ui/Modal';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import {
  ReceiptText, Clock, CheckCircle2, XCircle, ShieldCheck,
  RefreshCw, Ban, Check, X, ChevronLeft, ChevronRight,
  Calendar, Package, AlertTriangle, Loader2, FileText, MessageCircle,
  History, User, Mail,
} from 'lucide-react';
import './RefundRequests.css';

const STATUS_CONFIG = {
  PENDING_MERCHANT: { label: 'Awaiting review', icon: Clock,       pill: 'rf-pill-warn'    },
  PENDING_ADMIN:    { label: 'With admin',      icon: ShieldCheck, pill: 'rf-pill-info'    },
  PROCESSING:       { label: 'Processing',      icon: RefreshCw,   pill: 'rf-pill-purple'  },
  COMPLETED:        { label: 'Completed',       icon: CheckCircle2, pill: 'rf-pill-success' },
  REJECTED:         { label: 'Rejected',        icon: XCircle,     pill: 'rf-pill-danger'  },
};

const REJECT_REASONS = [
  'Product delivered as described',
  'Outside refund policy period',
  'Product was used or damaged by customer',
  'Request lacks sufficient evidence',
  'Duplicate request',
  'Other',
];

const APPROVAL_REASONS = [
  {
    code: 'CUSTOMER_CANCELLED',
    title: 'Customer cancelled',
    description: 'Customer cancelled before fulfillment.',
    policy: 'Refund items, tax, and delivery. Service charge stays non-refundable.',
  },
  {
    code: 'CUSTOMER_CHANGED_MIND',
    title: 'Customer changed mind',
    description: 'Customer no longer wants the order.',
    policy: 'Refund items, tax, and delivery. Service charge stays non-refundable.',
  },
  {
    code: 'ITEM_UNAVAILABLE',
    title: 'Item unavailable',
    description: 'The store cannot supply one or more items.',
    policy: 'Customer gets the full eligible amount. Merchant absorbs the service charge.',
  },
  {
    code: 'MERCHANT_CANNOT_FULFILL',
    title: 'Cannot fulfill order',
    description: 'The store cannot complete the order.',
    policy: 'Customer gets the full eligible amount. Merchant absorbs the service charge.',
  },
  {
    code: 'MERCHANT_CANCELLED',
    title: 'Merchant cancelled',
    description: 'The store cancelled the order.',
    policy: 'Customer gets the full eligible amount. Merchant absorbs the service charge.',
  },
];

const ITEMS_PER_PAGE = 25;

const RefundRequests = () => {
  const { isAuthenticated, loading: authLoading } = useSecureAuth();
  const toast = useToast();
  const canEdit = usePermission('refunds', 'edit');

  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveModal, setApproveModal] = useState({ open: false, request: null });
  const [approveReasonCode, setApproveReasonCode] = useState('CUSTOMER_CANCELLED');
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectModal, setRejectModal] = useState({ open: false, requestId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const loadRefundRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/refund-requests/merchant/all');
      if (response.data.success) {
        setRefundRequests(response.data.refunds || []);
      } else {
        setError('Failed to load refund requests');
      }
    } catch (err) {
      console.error('Load refund requests error:', err);
      setError(err.response?.data?.error || 'Failed to load refund requests');
    } finally {
      setLoading(false);
    }
  };

  const inferApprovalReason = (request) => {
    const reason = `${request?.refundReason || ''} ${request?.customerNotes || ''}`.toLowerCase();
    if (reason.includes('unavailable') || reason.includes('out of stock')) return 'ITEM_UNAVAILABLE';
    if (reason.includes('cannot fulfill') || reason.includes('unable to fulfill')) return 'MERCHANT_CANNOT_FULFILL';
    if (reason.includes('merchant cancelled') || reason.includes('store cancelled')) return 'MERCHANT_CANCELLED';
    if (reason.includes('changed mind') || reason.includes('no longer')) return 'CUSTOMER_CHANGED_MIND';
    return 'CUSTOMER_CANCELLED';
  };

  const openApproveModal = (request) => {
    setApproveModal({ open: true, request });
    setApproveReasonCode(inferApprovalReason(request));
    setApproveNotes('');
  };

  const closeApproveModal = () => {
    setApproveModal({ open: false, request: null });
    setApproveReasonCode('CUSTOMER_CANCELLED');
    setApproveNotes('');
  };

  const handleApprove = async () => {
    const request = approveModal.request;
    if (!request) return;

    try {
      setActionLoading(request.id);
      setError('');
      const response = await axios.post(`/refund-requests/merchant/approve/${request.id}`, {
        merchantNotes: approveNotes,
        refundReasonCode: approveReasonCode,
      });
      if (response.data.success) {
        toast.success('Refund approved and forwarded to admin');
        loadRefundRequests();
        if (selectedRequest?.id === request.id) setSelectedRequest(null);
        closeApproveModal();
      } else {
        setError(response.data.error || 'Failed to approve refund request');
      }
    } catch (err) {
      console.error('Approve refund error:', err);
      setError(err.response?.data?.error || 'Failed to approve refund request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please pick a rejection reason');
      return;
    }
    try {
      setActionLoading(rejectModal.requestId);
      setError('');
      const response = await axios.post(`/refund-requests/merchant/reject/${rejectModal.requestId}`, {
        rejectionReason: rejectReason,
        merchantNotes: rejectNotes,
      });
      if (response.data.success) {
        toast.success('Refund rejected');
        loadRefundRequests();
        if (selectedRequest?.id === rejectModal.requestId) setSelectedRequest(null);
        closeRejectModal();
      } else {
        setError(response.data.error || 'Failed to reject refund request');
      }
    } catch (err) {
      console.error('Reject refund error:', err);
      setError(err.response?.data?.error || 'Failed to reject refund request');
    } finally {
      setActionLoading(null);
    }
  };

  const closeRejectModal = () => {
    setRejectModal({ open: false, requestId: null });
    setRejectReason('');
    setRejectNotes('');
  };

  useEffect(() => {
    if (isAuthenticated) loadRefundRequests();
  }, [isAuthenticated]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortBy]);

  const filteredRequests = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return refundRequests
      .filter((r) => {
        if (statusFilter !== 'all' && r.status !== statusFilter) return false;
        if (!q) return true;
        return (
          r.order?.id?.toString().toLowerCase().includes(q) ||
          r.customer?.firstName?.toLowerCase().includes(q) ||
          r.customer?.lastName?.toLowerCase().includes(q) ||
          r.refundReason?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
          case 'amount_high': return (b.refundAmount || 0) - (a.refundAmount || 0);
          case 'amount_low':  return (a.refundAmount || 0) - (b.refundAmount || 0);
          default: return new Date(b.createdAt) - new Date(a.createdAt);
        }
      });
  }, [refundRequests, searchTerm, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRequests = filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const stats = useMemo(() => {
    const pending = refundRequests.filter((r) =>
      r.status === 'PENDING_MERCHANT' || r.status === 'PENDING_ADMIN' || r.status === 'PROCESSING'
    );
    return {
      pending: pending.length,
      pendingAmount: pending.reduce((s, r) => s + (r.refundAmount || 0), 0),
      completed: refundRequests.filter((r) => r.status === 'COMPLETED').length,
      rejected: refundRequests.filter((r) => r.status === 'REJECTED').length,
    };
  }, [refundRequests]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatDateTime = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const formatReason = (reason) => {
    if (!reason) return 'Not specified';
    return reason.charAt(0).toUpperCase() + reason.slice(1).replace(/_/g, ' ');
  };

  const statusOf = (status) => STATUS_CONFIG[status] || { label: status, icon: Clock, pill: 'rf-pill-muted' };

  if (!isAuthenticated && !authLoading) {
    return (
      <Shell>
        <div className="rf-page">
          <div className="rf-empty">
            <div className="rf-empty-icon"><ShieldCheck size={22} /></div>
            <h2 className="rf-empty-title">Sign in required</h2>
            <p className="rf-empty-desc">Please log in to view refund requests.</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <RoleGuard
        permission="refunds:view"
        fallback={
          <div className="rf-page">
            <div className="rf-empty">
              <div className="rf-empty-icon"><ShieldCheck size={22} /></div>
              <h2 className="rf-empty-title">No access</h2>
              <p className="rf-empty-desc">You don't have permission to view refund requests.</p>
            </div>
          </div>
        }
      >
        <div className="rf-page">

          {/* ── Header ─────────────────────────────────────────────── */}
          <header className="rf-header">
            <div className="rf-header-text">
              <h1 className="rf-title">Refund requests</h1>
              <p className="rf-subtitle">Approve or reject customer refunds. Approved requests go to Revstar Admin for processing.</p>
            </div>
            <div className="rf-header-actions">
              <button
                type="button"
                className="rf-btn rf-btn-ghost"
                onClick={loadRefundRequests}
                disabled={loading}
                aria-label="Refresh"
                title="Refresh"
              >
                <RefreshCw size={16} className={loading ? 'rf-spin' : ''} />
                <span className="rf-btn-text">Refresh</span>
              </button>
            </div>
          </header>

          {/* ── Error banner ───────────────────────────────────────── */}
          {error && (
            <div className="rf-banner rf-banner-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
              <button
                type="button"
                className="rf-banner-close"
                onClick={() => setError('')}
                aria-label="Dismiss"
              >×</button>
            </div>
          )}

          {/* ── Stat tiles (3-up) ──────────────────────────────────── */}
          <section className="rf-stat-grid">
            <div className="rf-stat">
              <span className="rf-stat-label">Pending</span>
              <span className="rf-stat-value">{stats.pending}</span>
              {stats.pendingAmount > 0 && (
                <span className="rf-stat-sub">{formatCurrency(stats.pendingAmount)} held</span>
              )}
            </div>
            <div className="rf-stat">
              <span className="rf-stat-label">Completed</span>
              <span className="rf-stat-value rf-stat-value-success">{stats.completed}</span>
            </div>
            <div className="rf-stat">
              <span className="rf-stat-label">Rejected</span>
              <span className="rf-stat-value rf-stat-value-danger">{stats.rejected}</span>
            </div>
          </section>

          {/* ── Filters ────────────────────────────────────────────── */}
          <section className="rf-filters">
            <div className="rf-search">
              <input
                type="text"
                className="rf-input rf-search-input"
                placeholder="Search by order, customer, or reason…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="rf-search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              className="rf-input rf-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All status</option>
              <option value="PENDING_MERCHANT">Awaiting review</option>
              <option value="PENDING_ADMIN">With admin</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <select
              className="rf-input rf-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="amount_high">Amount: high to low</option>
              <option value="amount_low">Amount: low to high</option>
            </select>
          </section>

          {/* ── Body ───────────────────────────────────────────────── */}
          {loading ? (
            <div className="rf-loading">
              <Loader2 size={20} className="rf-spin" />
              <span>Loading refund requests…</span>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="rf-empty">
              <div className="rf-empty-icon"><ReceiptText size={22} /></div>
              <h2 className="rf-empty-title">
                {refundRequests.length === 0 ? 'No refund requests yet' : 'No matches'}
              </h2>
              <p className="rf-empty-desc">
                {refundRequests.length === 0
                  ? 'Customers can request refunds from their order page — they\'ll show up here.'
                  : 'No requests match your current filters.'}
              </p>
              {(searchTerm || statusFilter !== 'all') && (
                <button
                  type="button"
                  className="rf-btn rf-btn-secondary"
                  onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="rf-results">
                Showing {paginatedRequests.length} of {filteredRequests.length} request{filteredRequests.length === 1 ? '' : 's'}
              </p>
              <ul className="rf-list">
                {paginatedRequests.map((request) => {
                  const status = statusOf(request.status);
                  const StatusIcon = status.icon;
                  return (
                    <li key={request.id} className="rf-row">
                      <button
                        type="button"
                        className="rf-row-main"
                        onClick={() => setSelectedRequest(request)}
                        aria-label={`View refund #${request.order?.id?.slice(-8) || ''}`}
                      >
                        <span className="rf-row-id">
                          #{request.order?.id?.slice(-8) || 'N/A'}
                        </span>
                        <span className="rf-row-customer">
                          {request.customer?.firstName} {request.customer?.lastName}
                        </span>
                        <span className="rf-row-reason">{formatReason(request.refundReason)}</span>
                        <span className={`rf-pill ${status.pill}`}>
                          <StatusIcon size={10} className={request.status === 'PROCESSING' ? 'rf-spin' : ''} />
                          {status.label}
                        </span>
                        <span className="rf-row-date">{formatDate(request.createdAt)}</span>
                        <span className="rf-row-amount">{formatCurrency(request.refundAmount)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {totalPages > 1 && (
                <nav className="rf-pagination" aria-label="Pagination">
                  <button
                    type="button"
                    className="rf-page-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="rf-page-info">
                    Page <strong>{currentPage}</strong> of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="rf-page-btn"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} />
                  </button>
                </nav>
              )}
            </>
          )}

          {/* ── Detail modal ───────────────────────────────────────── */}
          <Modal
            isOpen={!!selectedRequest}
            onClose={() => setSelectedRequest(null)}
            title={selectedRequest ? `Refund #${selectedRequest.order?.id?.slice(-8) || ''}` : ''}
            padded={false}
            footer={
              selectedRequest?.status === 'PENDING_MERCHANT' && canEdit ? (
                <>
                  <button
                    type="button"
                    className="rf-btn rf-btn-danger"
                    disabled={actionLoading === selectedRequest.id}
                    onClick={() => setRejectModal({ open: true, requestId: selectedRequest.id })}
                  >
                    <Ban size={14} />
                    Reject
                  </button>
                  <button
                    type="button"
                    className="rf-btn rf-btn-primary"
                    disabled={actionLoading === selectedRequest.id}
                    onClick={() => openApproveModal(selectedRequest)}
                  >
                    {actionLoading === selectedRequest.id
                      ? <Loader2 size={14} className="rf-spin" />
                      : <Check size={14} />}
                    Approve & forward
                  </button>
                </>
              ) : null
            }
          >
            {selectedRequest && (() => {
              const status = statusOf(selectedRequest.status);
              const StatusIcon = status.icon;
              return (
                <div className="rf-detail">
                  <div className="rf-detail-amount-row">
                    <div>
                      <span className="rf-detail-amount-label">Refund amount</span>
                      <div className="rf-detail-amount-value">{formatCurrency(selectedRequest.refundAmount)}</div>
                    </div>
                    <span className={`rf-pill ${status.pill}`}>
                      <StatusIcon size={11} className={selectedRequest.status === 'PROCESSING' ? 'rf-spin' : ''} />
                      {status.label}
                    </span>
                  </div>

                  <dl className="rf-detail-kv">
                    <div className="rf-detail-kv-row">
                      <dt><User size={13} /> Customer</dt>
                      <dd>{selectedRequest.customer?.firstName} {selectedRequest.customer?.lastName}</dd>
                    </div>
                    <div className="rf-detail-kv-row">
                      <dt><Mail size={13} /> Email</dt>
                      <dd>{selectedRequest.customer?.email || '—'}</dd>
                    </div>
                    <div className="rf-detail-kv-row">
                      <dt><Package size={13} /> Order</dt>
                      <dd className="rf-detail-mono">#{selectedRequest.order?.id || '—'}</dd>
                    </div>
                    <div className="rf-detail-kv-row">
                      <dt><Calendar size={13} /> Requested</dt>
                      <dd>{formatDateTime(selectedRequest.createdAt)}</dd>
                    </div>
                    <div className="rf-detail-kv-row">
                      <dt><FileText size={13} /> Reason</dt>
                      <dd>{formatReason(selectedRequest.refundReason)}</dd>
                    </div>
                  </dl>

                  {selectedRequest.customerNotes && (
                    <div className="rf-detail-section">
                      <h3 className="rf-detail-section-title">
                        <MessageCircle size={13} /> Customer notes
                      </h3>
                      <p className="rf-detail-notes">{selectedRequest.customerNotes}</p>
                    </div>
                  )}

                  {(selectedRequest.merchantResponse?.action || selectedRequest.adminResponse?.action) && (
                    <div className="rf-detail-section">
                      <h3 className="rf-detail-section-title">
                        <History size={13} /> Response history
                      </h3>
                      <div className="rf-history">
                        {selectedRequest.merchantResponse?.action && (
                          <ResponseEntry
                            actor="Merchant"
                            action={selectedRequest.merchantResponse.action}
                            at={selectedRequest.merchantResponse.respondedAt || selectedRequest.merchantResponse.approvedAt || selectedRequest.merchantResponse.rejectedAt}
                            notes={selectedRequest.merchantResponse.notes}
                            formatDateTime={formatDateTime}
                          />
                        )}
                        {selectedRequest.adminResponse?.action && (
                          <ResponseEntry
                            actor="Admin"
                            action={selectedRequest.adminResponse.action}
                            at={selectedRequest.adminResponse.respondedAt || selectedRequest.adminResponse.processedAt || selectedRequest.adminResponse.completedAt}
                            notes={selectedRequest.adminResponse.notes}
                            formatDateTime={formatDateTime}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {selectedRequest.status === 'PENDING_ADMIN' && (
                    <div className="rf-banner rf-banner-info rf-banner-block">
                      <ShieldCheck size={16} />
                      <div>
                        <strong>With Revstar Admin</strong>
                        <p>You'll be notified once admin processes this request.</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </Modal>

          {/* ── Approve modal ──────────────────────────────────────── */}
          <Modal
            isOpen={approveModal.open}
            onClose={closeApproveModal}
            title="Approve refund request"
            padded={false}
            footer={
              <>
                <button
                  type="button"
                  className="rf-btn rf-btn-secondary"
                  onClick={closeApproveModal}
                  disabled={!!actionLoading}
                >Cancel</button>
                <button
                  type="button"
                  className="rf-btn rf-btn-primary"
                  disabled={!!actionLoading}
                  onClick={handleApprove}
                >
                  {actionLoading
                    ? <Loader2 size={14} className="rf-spin" />
                    : <Check size={14} />}
                  Approve & forward
                </button>
              </>
            }
          >
            <div className="rf-form">
              <p className="rf-form-help">
                Choose why this refund is being approved. This controls how service charge is handled.
              </p>
              <div className="rf-field">
                <label className="rf-field-label">Approval reason</label>
                <div className="rf-reason-grid">
                  {APPROVAL_REASONS.map((reason) => (
                    <button
                      key={reason.code}
                      type="button"
                      className={`rf-reason-card ${approveReasonCode === reason.code ? 'is-selected' : ''}`}
                      onClick={() => setApproveReasonCode(reason.code)}
                    >
                      <span className="rf-reason-card-head">
                        <span>{reason.title}</span>
                        {approveReasonCode === reason.code && <CheckCircle2 size={15} />}
                      </span>
                      <span className="rf-reason-card-desc">{reason.description}</span>
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                const selectedReason = APPROVAL_REASONS.find((r) => r.code === approveReasonCode);
                return selectedReason ? (
                  <div className="rf-policy-note">
                    <AlertTriangle size={15} />
                    <span>{selectedReason.policy}</span>
                  </div>
                ) : null;
              })()}
              <div className="rf-field">
                <label className="rf-field-label">Admin notes (optional)</label>
                <textarea
                  className="rf-input rf-textarea"
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  placeholder="Add context for admin review…"
                  rows={3}
                />
              </div>
            </div>
          </Modal>

          {/* ── Reject modal ───────────────────────────────────────── */}
          <Modal
            isOpen={rejectModal.open}
            onClose={closeRejectModal}
            title="Reject refund request"
            padded={false}
            footer={
              <>
                <button
                  type="button"
                  className="rf-btn rf-btn-secondary"
                  onClick={closeRejectModal}
                >Cancel</button>
                <button
                  type="button"
                  className="rf-btn rf-btn-danger"
                  disabled={!rejectReason || !!actionLoading}
                  onClick={handleReject}
                >
                  {actionLoading
                    ? <Loader2 size={14} className="rf-spin" />
                    : <Ban size={14} />}
                  Reject
                </button>
              </>
            }
          >
            <div className="rf-form">
              <p className="rf-form-help">
                Pick a reason — the customer sees this in their order page and refund email.
              </p>
              <div className="rf-field">
                <label className="rf-field-label">Reason</label>
                <select
                  className="rf-input"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                >
                  <option value="">Select a reason…</option>
                  {REJECT_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="rf-field">
                <label className="rf-field-label">Additional notes (optional)</label>
                <textarea
                  className="rf-input rf-textarea"
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Add context the customer might find useful…"
                  rows={3}
                />
              </div>
            </div>
          </Modal>

        </div>
      </RoleGuard>
    </Shell>
  );
};

const ResponseEntry = ({ actor, action, at, notes, formatDateTime }) => {
  const normalizedAction = String(action || '').trim().toLowerCase();
  const approved = ['approve', 'approved', 'accept', 'accepted', 'processing', 'completed'].includes(normalizedAction);
  const rejected = ['reject', 'rejected', 'decline', 'declined'].includes(normalizedAction);
  const queued = normalizedAction === 'queued';
  const label = approved ? 'approved' : queued ? 'queued' : rejected ? 'rejected' : 'responded';
  const Icon = approved ? CheckCircle2 : queued ? Clock : rejected ? XCircle : History;
  return (
    <div className={`rf-history-item ${approved ? 'is-approved' : queued ? 'is-queued' : rejected ? 'is-rejected' : ''}`}>
      <div className="rf-history-head">
        <span className="rf-history-actor">
          <Icon size={13} />
          {actor} {label}
        </span>
        {at && <span className="rf-history-time">{formatDateTime(at)}</span>}
      </div>
      {notes && <p className="rf-history-notes">{notes}</p>}
    </div>
  );
};

export default RefundRequests;
